"use client";

import React from "react";
import { Cpu, PhoneCall, FileCheck, Wrench, Rocket, ArrowRight, Activity, Clock, TrendingUp, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [];
const reviewsPlaceholder = {
  title: "What Our Clients Are Saying",
  subtitle: "We take pride in providing the most thorough inspections in South Florida. See why hundreds of homeowners and realtors trust us with their property investments.",
  buttonText: "Read Our Verified Reviews"
};
const protocolFeatures = [{
  title: "24-Hour Reports",
  description: "Detailed digital reports with clear photos and findings delivered within 24 hours—often same day.",
  icon: Clock
}, {
  title: "Expert Precision",
  description: "Licensed Florida inspector identifying roof, plumbing, electrical, HVAC, and moisture issues before they become expensive problems.",
  icon: Cpu
}, {
  title: "Market Confidence",
  description: "Clear, unbiased evaluations that give buyers and sellers the confidence to make informed real estate decisions.",
  icon: TrendingUp
}];
function StepCard({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div data-section-id="159520" data-index={index} ref={ref} className={`
        ${step.colSpan} 
        transition-all duration-1000 ease-out
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}
        ${index > 0 ? 'mt-8 md:mt-24' : ''}
      `}>
      <Card className="bg-card dark:bg-card backdrop-blur-none border-border shadow-lg overflow-hidden relative group hover:border-primary/50 transition-colors duration-500">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
        
        <CardHeader className="pb-4 relative z-10">
          <div className="flex items-center justify-between mb-4">
            <Badge variant="outline" className="font-mono text-primary border-primary/20 bg-primary/5 tracking-widest">
              VERIFIED
            </Badge>
            <step.icon className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors duration-300" />
          </div>
          <div className="space-y-1">
            <p className="font-sans text-xs text-muted-foreground font-medium tracking-widest uppercase">
              {step.label}
            </p>
            <CardTitle className="text-2xl font-medium tracking-tight text-foreground">
              {step.heading}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="relative z-10">
          <CardDescription className="font-sans text-sm leading-relaxed text-muted-foreground font-normal">
            {step.description}
          </CardDescription>
        </CardContent>
      </Card>
    </div>;
}
export default function HowItWorks() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: protocolRef,
    isVisible: isProtocolVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="clientreviews" className="bg-background py-[160px] relative overflow-hidden">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        <div ref={headerRef} className={`max-w-5xl mx-auto text-center mb-24 transition-all duration-1000 ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Badge variant="secondary" className="mb-6 font-mono tracking-widest uppercase bg-primary/10 text-primary border-primary/20 backdrop-blur-sm">
            Client Reviews
          </Badge>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter mb-6 text-secondary uppercase">
            Trusted by South Florida <br className="hidden md:block" />
            <span className="text-foreground">
              Buyers, Realtors, and Homeowners
            </span>
          </h2>
          <p className="text-lg text-muted-foreground font-sans max-w-2xl mx-auto mb-16">
            Clear communication, detailed reports, and dependable service from inspection to closing.
          </p>

          <Card className="max-w-3xl mx-auto text-center p-12 border-dashed border-2 border-primary/20 bg-primary/5">
            <CardHeader>
              <div className="flex justify-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-primary text-primary" />)}
              </div>
              <CardTitle className="text-3xl font-bold text-foreground mb-4">
                {reviewsPlaceholder.title}
              </CardTitle>
              <CardDescription className="text-lg text-muted-foreground max-w-xl mx-auto">
                {reviewsPlaceholder.subtitle}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-10">
                <Link to="/HomeInspectionServicePage">
                  {reviewsPlaceholder.buttonText}
                </Link>
              </Button>
            </CardContent>
          </Card>

          <div className="mt-12 flex justify-center">
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8">
              <Link to="/HomeInspectionServicePage">
                Read Our Google Reviews
              </Link>
            </Button>
          </div>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 mb-32">
          <div className="hidden md:block absolute left-1/2 top-8 bottom-8 w-px bg-gradient-to-b from-transparent via-primary/20 to-transparent -translate-x-1/2" />
          
          {steps.map((step, index) => <StepCard key={step.id} step={step} index={index} />)}
        </div>

        <div ref={protocolRef} className={`transition-all duration-1000 delay-300 ${isProtocolVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <Separator className="bg-border mb-12" />
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 space-y-6">
              <div>
                <h3 className="text-2xl font-bold tracking-tight text-foreground mb-2">
                  The Trusted Standard
                </h3>
                <p className="font-sans text-sm text-muted-foreground font-medium">
                  Our clear, photo‑rich inspection approach protects your investment.
                </p>
              </div>
              <p className="text-muted-foreground leading-relaxed font-sans">
                We perform every inspection with one goal: giving you the clear, honest information you need for confident real estate decisions. Whether you're buying, selling, or meeting insurance requirements, Trusted Inspections delivers professional service backed by 5+ years of South Florida experience.
              </p>
              <Button asChild size="lg" className="w-full sm:w-auto group bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link to="/HomeInspectionInHomesteadFLPage">
                  Schedule Your Inspection
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
            
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-8">
              {protocolFeatures.map((feature, index) => <div key={index} data-index={index} className="space-y-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h4 className="font-bold text-foreground">{feature.title}</h4>
                  <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>)}
            </div>
          </div>
          
          <Separator className="bg-border mt-12" />
        </div>

      </div>
    </section>;
}